/* eslint-disable */
import { useState } from 'react'
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Alert,
  IconButton,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel
} from '@mui/material'
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  VpnKey as VpnKeyIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material'
import { authAPI } from '../../services/api'
import { useToast } from '../../context/ToastContext'

const steps = ['Enter Email', 'Verify OTP', 'Reset Password']

export function ForgotPasswordPage({ onBackToLogin, onBackToHome }) {
  const toast = useToast()

  // Step management
  const [activeStep, setActiveStep] = useState(0)

  // Step 1: Email input
  const [email, setEmail] = useState('')

  // Step 2: OTP verification
  const [otp, setOtp] = useState('')
  const [resetToken, setResetToken] = useState('')

  // Step 3: New password
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // State management
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const clearMessages = () => {
    setErrors({})
  }

  const handleInputChange = (field) => (event) => {
    const value = event.target.value

    switch (field) {
      case 'email':
        setEmail(value)
        break
      case 'otp':
        // Only allow numbers and limit to 6 digits
        if (/^\d*$/.test(value) && value.length <= 6) {
          setOtp(value)
        }
        break
      case 'newPassword':
        setNewPassword(value)
        break
      case 'confirmPassword':
        setConfirmPassword(value)
        break
      default:
        break
    }

    clearMessages()
  }

  // Step 1: Request OTP
  const handleRequestOTP = async (event) => {
    event.preventDefault()
    clearMessages()

    // Validate email
    if (!email) {
      setErrors({ email: 'Email is required' })
      return
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Please enter a valid email address' })
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.forgotPassword(email)

      if (response.data.success) {
        toast.showSuccess(response.data.message || 'OTP sent successfully to your email!')
        setActiveStep(1) // Move to OTP verification step
      } else {
        toast.showError(response.data.message || 'Failed to send OTP. Please try again.')
      }
    } catch (error) {
      console.error('Request OTP error:', error)

      if (error.response?.status === 429) {
        toast.showError(error.response.data.message || 'Too many requests. Please try again later.')
      } else if (error.response?.data?.message) {
        toast.showError(error.response.data.message)
      } else {
        toast.showError('Failed to send OTP. Please check your connection and try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Step 2: Verify OTP
  const handleVerifyOTP = async (event) => {
    event.preventDefault()
    clearMessages()

    // Validate OTP
    if (!otp) {
      setErrors({ otp: 'OTP is required' })
      return
    }
    if (otp.length !== 6) {
      setErrors({ otp: 'OTP must be 6 digits' })
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.verifyOTP(email, otp)

      if (response.data.success) {
        setResetToken(response.data.data.resetToken)
        toast.showSuccess(response.data.message || 'OTP verified successfully!')
        setActiveStep(2) // Move to reset password step
      } else {
        toast.showError(response.data.message || 'Invalid OTP. Please try again.')
      }
    } catch (error) {
      console.error('Verify OTP error:', error)

      if (error.response?.data?.message) {
        toast.showError(error.response.data.message)
      } else {
        toast.showError('Failed to verify OTP. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Step 3: Reset Password
  const handleResetPassword = async (event) => {
    event.preventDefault()
    clearMessages()

    // Validate passwords
    const newErrors = {}

    if (!newPassword) {
      newErrors.newPassword = 'Password is required'
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters'
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.resetPassword(resetToken, newPassword)

      if (response.data.success) {
        toast.showSuccess(response.data.message || 'Password reset successfully!')
        setActiveStep(3) // Move to success screen

        // Redirect to login after 3 seconds
        setTimeout(() => {
          onBackToLogin()
        }, 3000)
      } else {
        toast.showError(response.data.message || 'Failed to reset password. Please try again.')
      }
    } catch (error) {
      console.error('Reset password error:', error)

      if (error.response?.status === 401) {
        toast.showError('Reset session expired. Please request a new OTP.')
        // Go back to step 1
        setTimeout(() => {
          handleStartOver()
        }, 2000)
      } else if (error.response?.data?.message) {
        toast.showError(error.response.data.message)
      } else {
        toast.showError('Failed to reset password. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setOtp('')
    await handleRequestOTP({ preventDefault: () => {} })
  }

  const handleStartOver = () => {
    setActiveStep(0)
    setEmail('')
    setOtp('')
    setResetToken('')
    setNewPassword('')
    setConfirmPassword('')
    clearMessages()
  }

  // Success screen (Step 4)
  if (activeStep === 3) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={8}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 4
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />

            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Password Reset Successful!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your password has been reset successfully. You will be redirected to the login page in a few seconds.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={onBackToLogin}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 4
          }}
        >
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={activeStep === 0 ? onBackToLogin : handleStartOver}
            sx={{ mb: 3, color: 'text.secondary' }}
          >
            {activeStep === 0 ? 'Back to Login' : 'Start Over'}
          </Button>

          {/* Header */}
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, textAlign: 'center' }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            {activeStep === 0 && "Enter your email to receive an OTP"}
            {activeStep === 1 && "Enter the 6-digit OTP sent to your email"}
            {activeStep === 2 && "Create a new secure password"}
          </Typography>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Email Input */}
          {activeStep === 0 && (
            <Box component="form" onSubmit={handleRequestOTP}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                disabled={isLoading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.5, mb: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Send OTP'}
              </Button>
            </Box>
          )}

          {/* Step 2: OTP Verification */}
          {activeStep === 1 && (
            <Box component="form" onSubmit={handleVerifyOTP}>
              <Alert severity="info" sx={{ mb: 3 }}>
                An OTP has been sent to <strong>{email}</strong>. Please check your email inbox.
              </Alert>

              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={handleInputChange('otp')}
                error={!!errors.otp}
                helperText={errors.otp || 'Enter the 6-digit code'}
                disabled={isLoading}
                inputProps={{
                  maxLength: 6,
                  style: { fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' }
                }}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || otp.length !== 6}
                sx={{ py: 1.5, mb: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify OTP'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={handleResendOTP}
                disabled={isLoading}
              >
                Didn't receive the OTP? Resend
              </Button>
            </Box>
          )}

          {/* Step 3: Reset Password */}
          {activeStep === 2 && (
            <Box component="form" onSubmit={handleResetPassword}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleInputChange('newPassword')}
                error={!!errors.newPassword}
                helperText={errors.newPassword || 'At least 8 characters with uppercase, lowercase, and number'}
                disabled={isLoading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                disabled={isLoading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{' '}
              <Button
                variant="text"
                onClick={onBackToLogin}
                sx={{
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                  p: 0,
                  fontSize: 'inherit'
                }}
              >
                Sign in here
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
